import axios from 'axios';
import { fetchWithOfflineSupport } from '../../helpers/offlineApi';
import { setLoading, setMessage } from './status';

export const SET_PROFILES = 'SET_PROFILES';
export const CLEAR_PROFILES = 'CLEAR_PROFILES';

const API_BASE_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';

export const fetchProfiles = (filters = {}, preferOffline = false) => async dispatch => {
    dispatch(setLoading(true));

    try {
        const result = await fetchWithOfflineSupport(
            async () => {
                const response = await axios.get(`${API_BASE_URL}/profiles`, {
                    params: {
                        limit: filters.limit || 1000,
                        offset: filters.offset || 0,
                        name: filters.search ? filters.search.toLowerCase() : ''
                    },
                    timeout: 10000
                });
                return response.data.profiles || response.data || [];
            },
            async () => {
                return [];
            },
            preferOffline
        );

        dispatch({
            type: SET_PROFILES,
            payload: result
        });

        if (result.source === 'offline') {
            dispatch(setMessage({
               // message: 'Profils chargés depuis le cache hors ligne',
                status: 'info'
            }));
        }

        return result;
    } catch (error) {
        dispatch(setMessage({
            message: 'Impossible de charger les profils',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const analyzeProfile = (profileData) => async dispatch => {
    if (!profileData || !profileData.age || !profileData.conditions) {
        dispatch(setMessage({
            message: 'Données de profil incomplètes pour l\'analyse',
            status: 'danger'
        }));
        return null;
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.post(`${API_BASE_URL}/profiles/analyze`, profileData, {
            timeout: 15000
        });

        const analysis = response.data;

        if (analysis.contraindications && analysis.contraindications.length > 0) {
            dispatch(setMessage({
                message: `${analysis.contraindications.length} contre-indication(s) détectée(s)`,
                status: 'warning'
            }));
        } else {
            dispatch(setMessage({
                message: 'Aucune contre-indication détectée pour ce profil',
                status: 'success'
            }));
        }

        return analysis;

    } catch (error) {
        dispatch(setMessage({
            message: 'Impossible d\'analyser le profil (fonctionnalité en ligne uniquement)',
            status: 'danger'
        }));
        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

export const analyzeByClasse = (classe, profileData) => async dispatch => {
    if (!classe || !profileData) {
        dispatch(setMessage({
            message: 'Classe et profil patient requis',
            status: 'danger'
        }));
        return [];
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.post(`${API_BASE_URL}/profiles/analyze-classe`, {
            classe,
            profile: profileData
        }, {
            timeout: 12000
        });

        const results = response.data.data || [];

        if (results.length > 0) {
            dispatch(setMessage({
                message: `${results.length} contre-indication(s) trouvée(s) pour la classe ${classe}`,
                status: 'warning'
            }));
        } else {
            dispatch(setMessage({
                message: `Aucune contre-indication pour la classe ${classe}`,
                status: 'success'
            }));
        }

        return results;

    } catch (error) {
        dispatch(setMessage({
            message: 'Erreur lors de l\'analyse par classe',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const analyzeByDCI = (dci, profileData) => async dispatch => {
    if (!dci || !profileData) {
        dispatch(setMessage({
            message: 'Veuillez fournir une DCI et des données pour l\'analyse',
            status: 'warning'
        }));
        return [];
    }

    dispatch(setLoading(true));

    try {
        // Augmentation du timeout pour éviter les erreurs de timeout
        const response = await axios.post(`${API_BASE_URL}/profiles/analyze-dci`, {
            dci,
            profile: profileData
        }, {
            timeout: 30000, // 30 secondes
            retry: 2,
            retryDelay: 1000
        });

        const results = response.data.data || [];

        let message = '';
        if (results.length > 0) {
            message = results.length === 1
                ? `⚠️ Une interaction détectée avec ${dci}`
                : `⚠️ ${results.length} interactions détectées avec ${dci}`;

            if (profileData.DCI) {
                message += ` et ${profileData.DCI}`;
            }

            dispatch(setMessage({
                message,
                status: 'warning'
            }));
        } else {
            message = `✅ Aucune interaction détectée avec ${dci}`;
            if (profileData.DCI) {
                message += ` et ${profileData.DCI}`;
            }

            dispatch(setMessage({
                message,
                status: 'success'
            }));
        }

        return results;

    } catch (error) {

        let errorMessage = 'Erreur lors de l\'analyse des interactions';

        if (error.code === 'ECONNABORTED') {
            errorMessage = 'L\'analyse a pris trop de temps. Veuillez réessayer.';
        } else if (error.response) {
            switch (error.response.status) {
                case 404:
                    errorMessage = 'DCI non trouvée dans la base de données';
                    break;
                case 400:
                    errorMessage = 'Données d\'analyse invalides';
                    break;
                case 500:
                    errorMessage = 'Erreur serveur lors de l\'analyse';
                    break;
                default:
                    errorMessage = error.response.data?.message || errorMessage;
            }
        }

        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));

        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const analyzeByNomCommercial = (nomCommercial, profileData) => async dispatch => {
    if (!nomCommercial || !profileData) {
        dispatch(setMessage({
            message: 'Veuillez fournir un nom commercial et un profil patient valides',
            status: 'warning'
        }));
        return [];
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.post(`${API_BASE_URL}/profiles/analyze-commercial`, {
            nomCommercial,
            profile: profileData
        }, {
            timeout: 30000, // Augmentation du timeout à 30 secondes
            retry: 2, // Ajout de tentatives de réessai
            retryDelay: 1000
        });

        const results = response.data.data || [];

        if (results.length > 0) {
            const message = results.length === 1
                ? `Une contre-indication détectée pour ${nomCommercial}`
                : `${results.length} contre-indications détectées pour ${nomCommercial}`;

            dispatch(setMessage({
                message,
                status: 'warning'
            }));
        } else {
            dispatch(setMessage({
                message: `Aucune contre-indication trouvée pour ${nomCommercial}`,
                status: 'success'
            }));
        }

        return results;

    } catch (error) {
        const errorMessage = error.code === 'ECONNABORTED'
            ? `L'analyse de ${nomCommercial} a pris trop de temps. Veuillez réessayer.`
            : `Erreur lors de l'analyse de ${nomCommercial}. ${error.response?.data?.message || ''}`;

        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));

        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const clearProfiles = () => ({
    type: CLEAR_PROFILES
});

export const getProfilesByName = (name, limit = 20) => async dispatch => {
    return dispatch(fetchProfiles({ search: name, limit }));
};

export const setProfiles = (profiles) => ({
    type: SET_PROFILES,
    payload: profiles
});

export default {
    fetchProfiles,
    getProfilesByName,
    setProfiles,
    analyzeProfile,
    analyzeByClasse,
    analyzeByDCI,
    analyzeByNomCommercial,
    clearProfiles
};